/**
 * LandlordDiscounts — smart discount campaigns for new tenants.
 *
 * Route: /discounts  (landlord-only, paid)
 *
 * Flow:
 *   1. Landlord sets a discount (% or flat CAD off, applies to first N months)
 *   2. When a new lease is created for that landlord, the system auto-applies
 *      the newest matching discount to the first-month rent
 *   3. Landlord sees a live counter of how many tenants have redeemed
 *
 * No promo codes — no forgotten spreadsheets. The system does the math.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/friendlyError";
import { toast } from "sonner";
import { Percent, DollarSign, Trash2, Sparkles, TicketPercent, Users, ShieldCheck } from "lucide-react";
import BackToPortalButton from "@/components/BackToPortalButton";

export default function LandlordDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    kind: "percent",
    value: "",
    scope: "new_tenant",
    applies_to_months: 1,
    max_uses: "",
    valid_until: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/landlord/discounts?include_inactive=true");
      setDiscounts(data?.items || []);
    } catch (e) {
      toast.error(friendlyError(e, "Couldn't load discounts"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.value) {
      toast.error("Give the discount a name and a value.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        kind: form.kind,
        value: parseFloat(form.value),
        scope: form.scope,
        applies_to_months: parseInt(form.applies_to_months, 10) || 1,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        valid_until: form.valid_until || null,
        notes: form.notes.trim(),
      };
      await api.post("/landlord/discounts", payload);
      toast.success("Discount live — new tenants will get it automatically.");
      setForm({ name: "", kind: "percent", value: "", scope: "new_tenant", applies_to_months: 1, max_uses: "", valid_until: "", notes: "" });
      load();
    } catch (err) {
      toast.error(friendlyError(err, "Couldn't save discount."));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this discount? It will stop applying to new leases.")) return;
    try {
      await api.delete(`/landlord/discounts/${id}`);
      toast.success("Deactivated.");
      load();
    } catch {
      toast.error("Couldn't deactivate.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landlord-discounts-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            <Sparkles className="w-3.5 h-3.5"/> Smart Feature
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 mt-3">Auto move-in discounts</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Set a discount once — every new tenant lease we generate for you automatically applies it. No promo codes, no forgotten spreadsheets. Uses first-match by newest discount.</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 grid lg:grid-cols-5 gap-6">
        {/* Create form */}
        <form onSubmit={submit} className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6 h-fit" data-testid="discount-create-form">
          <div className="flex items-center gap-2 mb-4">
            <TicketPercent className="w-5 h-5 text-emerald-600"/>
            <h2 className="font-display text-lg font-extrabold text-slate-900">New discount campaign</h2>
          </div>
          <div className="space-y-4">
            <Field label="Campaign name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Spring move-in bonus" testid="discount-name"/>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Discount type</label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="discount-kind" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none">
                  <option value="percent">Percent (%)</option>
                  <option value="flat">Flat CAD ($)</option>
                </select>
              </div>
              <Field
                label={form.kind === "percent" ? "Value (%)" : "Value (CAD)"}
                required type="number" step={form.kind === "percent" ? 1 : 5} min={0}
                value={form.value} onChange={(v) => setForm({ ...form, value: v })}
                placeholder={form.kind === "percent" ? "e.g. 15" : "e.g. 250"} testid="discount-value"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Eligible tenants</label>
                <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="discount-scope" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none">
                  <option value="new_tenant">New tenants only</option>
                  <option value="renewal">Renewals only</option>
                  <option value="any">Anyone</option>
                </select>
              </div>
              <Field label="Months covered" type="number" min={1} max={12} value={form.applies_to_months} onChange={(v) => setForm({ ...form, applies_to_months: v })} testid="discount-months"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Max redemptions (blank = unlimited)" type="number" min={1} value={form.max_uses} onChange={(v) => setForm({ ...form, max_uses: v })} placeholder="e.g. 10" testid="discount-max"/>
              <Field label="Expires on (blank = never)" type="date" value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} testid="discount-expires"/>
            </div>
            <Field label="Internal notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} placeholder="Optional" testid="discount-notes"/>
          </div>
          <button type="submit" disabled={saving} data-testid="discount-submit" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-extrabold disabled:opacity-50">
            {saving ? "Saving…" : (<><Sparkles className="w-4 h-4"/> Save & auto-apply</>)}
          </button>
          <p className="mt-3 text-xs text-slate-500 flex items-start gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-none"/>
            Discount auto-applies to the first-month rent of every new lease we generate. Tenants see the reduced amount in their portal — no promo code needed.
          </p>
        </form>

        {/* List of existing discounts */}
        <div className="lg:col-span-3 space-y-3" data-testid="discount-list">
          <h2 className="font-display text-lg font-extrabold text-slate-900">Your discounts</h2>
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && discounts.length === 0 && (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center bg-white" data-testid="discount-empty">
              <TicketPercent className="w-8 h-8 mx-auto text-slate-300"/>
              <p className="mt-3 font-display font-bold text-slate-800">No discounts yet</p>
              <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">Create your first campaign on the left. Central Rentals will auto-apply it to every new lease you sign.</p>
            </div>
          )}
          {discounts.map((d) => (
            <div key={d.id} className={`rounded-2xl border p-5 ${d.active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-60"}`} data-testid={`discount-row-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-extrabold text-slate-900 text-base">{d.name}</span>
                    {d.active
                      ? <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Active</span>
                      : <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">Inactive</span>}
                    <span className="text-[10px] uppercase tracking-widest font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
                      {d.scope === "new_tenant" ? "New tenants" : d.scope === "renewal" ? "Renewals" : "Anyone"}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm">
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                      {d.kind === "percent" ? <Percent className="w-3.5 h-3.5"/> : <DollarSign className="w-3.5 h-3.5"/>}
                      {d.kind === "percent" ? `${d.value}%` : `$${d.value} CAD`} off
                    </span>
                    <span className="text-slate-500">for {d.applies_to_months} month{d.applies_to_months === 1 ? "" : "s"}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3"/> {d.uses || 0} redeemed{d.max_uses ? ` / ${d.max_uses}` : ""}</span>
                    {d.valid_until && <span>· Expires {new Date(d.valid_until).toLocaleDateString()}</span>}
                    {d.notes && <span className="italic">· {d.notes}</span>}
                  </div>
                </div>
                {d.active && (
                  <button type="button" onClick={() => deactivate(d.id)} data-testid={`discount-delete-${d.id}`} className="text-slate-400 hover:text-rose-600 p-2 rounded-lg hover:bg-rose-50" aria-label="Deactivate">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
      <BackToPortalButton />
    </div>
  );
}

function Field({ label, value, onChange, testid, required, type = "text", ...rest }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600">
        {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        required={required}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        {...rest}
      />
    </div>
  );
}
